import { alpha, Chip, type SxProps, type Theme } from '@mui/material'
import React from 'react'
import { T } from '@/helpers/T'
import type { TMessageID } from '@/locales/types'
import type { TShareholderMembershipType } from '@/modules/Shareholder/Shareholder'

const TYPE_THEME: Record<
  TShareholderMembershipType,
  {
    label: TMessageID
    background: (theme: Theme) => string
    color: (theme: Theme) => string
    border: (theme: Theme) => string
  }
> = {
  GOLD: {
    label: 'm.shareholder.type.gold',
    background: (t) => alpha(t.palette.warning.main, 0.12),
    color: (t) => t.palette.warning.dark,
    border: (t) => alpha(t.palette.warning.main, 0.32),
  },
  SILVER: {
    label: 'm.shareholder.type.silver',
    background: (t) => alpha(t.palette.text.primary, 0.06),
    color: (t) => t.palette.text.secondary,
    border: (t) => alpha(t.palette.text.primary, 0.16),
  },
  OTHER: {
    label: 'm.shareholder.type.other',
    background: (t) => alpha(t.palette.info.main, 0.1),
    color: (t) => t.palette.info.dark,
    border: (t) => alpha(t.palette.info.main, 0.28),
  },
}

interface IProps {
  membershipType?: TShareholderMembershipType | null
  sx?: SxProps<Theme>
}

const ShareholderTypeChipInternal: React.FC<IProps> = ({ membershipType, sx }) => {
  const baseSx: SxProps<Theme> = {
    height: 20,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    flexShrink: 0,
    '& .MuiChip-label': { px: 1 },
  }

  if (!membershipType) {
    return (
      <Chip
        size='small'
        label={<T id='m.shareholder.status.inactive' />}
        variant='outlined'
        sx={[
          baseSx,
          {
            color: 'text.disabled',
            borderColor: (t) => alpha(t.palette.text.primary, 0.16),
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      />
    )
  }

  const typeTheme = TYPE_THEME[membershipType]

  return (
    <Chip
      size='small'
      label={<T id={typeTheme.label} />}
      sx={[
        (theme) => ({
          ...baseSx,
          bgcolor: typeTheme.background(theme),
          color: typeTheme.color(theme),
          border: `1px solid ${typeTheme.border(theme)}`,
          '&:hover': { bgcolor: typeTheme.background(theme) },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  )
}

export const ShareholderTypeChip = React.memo(ShareholderTypeChipInternal)
